import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Upload, FileText, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export function BulkStudentImport({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committed, setCommitted] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setCommitted(null); };
  const close = () => { if (!busy) { reset(); onOpenChange(false); } };

  const downloadTemplate = () => {
    const csv = [
      "student_ref,name,email,phone,company,country,tax_id",
      "CRM-000123,Ahmad Bin Ali,ahmad@example.com,+60123456789,Elite Marketing Sdn Bhd,MY,",
      "CRM-000124,Siti Binti Rahman,siti@example.com,+60129876543,,MY,",
    ].join("\n") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "student-import-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const runPreview = async (selected) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", selected);
      fd.append("dry_run", "true");
      const { data } = await api.post("/customers/bulk-import", fd);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "false");
      const { data } = await api.post("/customers/bulk-import", fd);
      setCommitted(data);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
      onImported?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Import students from CRM CSV</DialogTitle>
          <DialogDescription>
            Required column: <code className="font-mono">student_ref</code> (or "Student ID"). Optional: name, email, phone, company, country, tax_id. Matches existing students by Student ID and updates them; otherwise creates new.
          </DialogDescription>
          <button
            type="button"
            onClick={downloadTemplate}
            className="text-xs text-blue-700 underline decoration-dotted hover:text-blue-800 self-start mt-1"
            data-testid="bulk-import-template"
          >
            Download CSV template
          </button>
        </DialogHeader>

        {!file && (
          <label
            className="border-2 border-dashed border-slate-300 rounded p-8 text-center cursor-pointer hover:bg-slate-50 block"
            data-testid="bulk-import-dropzone"
          >
            <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <div className="text-sm font-medium">Choose a CSV file</div>
            <div className="text-xs text-slate-500 mt-1">Up to 2 MB · 5000 rows</div>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="bulk-import-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setFile(f); runPreview(f); }
              }}
            />
          </label>
        )}

        {file && (
          <div className="text-sm flex items-center justify-between border rounded p-3 bg-slate-50" data-testid="bulk-import-file-info">
            <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" /><span className="font-mono text-xs">{file.name}</span><span className="text-xs text-slate-500">{Math.round(file.size / 1024)} KB</span></div>
            {!committed && <Button variant="ghost" size="sm" onClick={() => { reset(); }}>Choose another</Button>}
          </div>
        )}

        {busy && <p className="text-sm text-slate-600" data-testid="bulk-import-busy">Analyzing…</p>}

        {preview && !committed && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 text-center text-sm">
              <Stat label="Total rows" value={preview.total_rows} testId="bulk-stat-total" />
              <Stat label="New" value={preview.to_create} tone="emerald" testId="bulk-stat-create" />
              <Stat label="Update" value={preview.to_update} tone="blue" testId="bulk-stat-update" />
              <Stat label="Conflicts" value={preview.conflicts} tone="rose" testId="bulk-stat-conflict" />
            </div>
            {preview.preview.conflicts.length > 0 && (
              <PreviewList
                title="Conflicts (will be skipped)"
                icon={<AlertTriangle className="w-4 h-4 text-rose-600" />}
                items={preview.preview.conflicts}
                testId="bulk-preview-conflicts"
                render={(r) => <span><span className="font-mono">row {r.row}</span> · {r.reason}</span>}
              />
            )}
            {preview.preview.update.length > 0 && (
              <PreviewList
                title="Will update existing students"
                icon={<CheckCircle2 className="w-4 h-4 text-blue-600" />}
                items={preview.preview.update}
                testId="bulk-preview-update"
                render={(r) => (
                  <span>
                    <span className="font-mono">{r.student_ref}</span> → {r.existing_name}
                    {r.elitebase_locked && <span className="ml-1 text-blue-700 text-[10px]">(EliteBase-locked · only ID synced)</span>}
                  </span>
                )}
              />
            )}
            {preview.preview.create.length > 0 && (
              <PreviewList
                title="Will create new students"
                icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                items={preview.preview.create}
                testId="bulk-preview-create"
                render={(r) => <span><span className="font-mono">{r.student_ref}</span> · {r.name}</span>}
              />
            )}
          </div>
        )}

        {committed && (
          <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-sm" data-testid="bulk-import-committed">
            <div className="font-semibold text-emerald-900">Import finished</div>
            <div className="text-emerald-800 mt-1">Created {committed.created} · Updated {committed.updated} · Conflicts {committed.conflicts}</div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>Close</Button>
          {preview && !committed && (
            <Button
              onClick={commit}
              disabled={busy || (preview.to_create === 0 && preview.to_update === 0)}
              data-testid="bulk-import-commit"
            >
              {busy ? "Importing…" : `Import ${preview.to_create + preview.to_update} student(s)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone = "slate", testId }) {
  const toneClass = {
    slate: "border-slate-200 bg-slate-50 text-slate-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  }[tone];
  return (
    <div className={`border rounded p-2 ${toneClass}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest opacity-75">{label}</div>
      <div className="font-mono text-xl tabular-nums">{value}</div>
    </div>
  );
}

function PreviewList({ title, icon, items, testId, render }) {
  return (
    <details className="border border-slate-200 rounded" data-testid={testId} open>
      <summary className="text-xs font-medium px-3 py-2 flex items-center gap-2 cursor-pointer bg-slate-50">
        {icon}
        {title} ({items.length})
      </summary>
      <ul className="max-h-40 overflow-y-auto divide-y divide-slate-100 text-xs">
        {items.map((r, i) => (
          <li key={r.row ?? r.student_ref ?? i} className="px-3 py-1.5" data-testid={`${testId}-row-${i}`}>{render(r)}</li>
        ))}
      </ul>
    </details>
  );
}
